import type {ReactElement} from 'react';
import {useEffect, useState} from 'react';
import {createPortal} from 'react-dom';
import {Text, TextSize} from '@Components/text';
import {selectRemoteSessionsOnPage} from '@Components/poster-editor/poster-presence-reducer';
import {getPageItemTransformVersion} from '@Components/poster-editor/poster-session-reducer';
import {Page} from '@PosterWhiteboard/page/page.class';
import type {RemoteSession} from '@PosterWhiteboard/collaboration/poster-presence.types';
import {useAppSelector} from '@/hooks';
import type {PagePresenceLayerProps} from './page-presence-layer.types';
import {getRemoteSelectionOutlines} from './page-presence-layer.library';
import styles from './page-presence-layer.module.scss';

/**
 * The other editors' cursors and selections on one page, drawn inside the page's canvas backdrop
 * so they inherit its position and the CSS scale of inactive pages. Remote positions are canvas
 * pixels; the layer multiplies them by this viewer's zoom.
 */
export function PagePresenceLayer({pageId}: PagePresenceLayerProps): ReactElement | null {
  const sessions = useAppSelector((state) => selectRemoteSessionsOnPage(state, pageId));
  const zoom = useAppSelector((state) => state.posterEditor.posterObject?.scaling.scale ?? 1);
  const pageOrder = useAppSelector((state) => state.posterEditor.posterObject?.pages.pageOrder);
  const itemTransformVersion = useAppSelector((state) => getPageItemTransformVersion(state, pageId));
  const [backdrop, setBackdrop] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    setBackdrop(Page.getCanvasBackdropForPageId(pageId) ?? null);
  }, [pageId, pageOrder]);

  if (backdrop === null) {
    return null;
  }

  const renderSelection = (session: RemoteSession): ReactElement[] => {
    return getRemoteSelectionOutlines(session.selectedItemIds, itemTransformVersion).map((outline) => (
      <div
        key={`${session.sessionId}-${outline.itemId}`}
        className={styles.selection}
        style={{borderColor: session.color, left: outline.left * zoom, top: outline.top * zoom, width: outline.width * zoom, height: outline.height * zoom}}
      />
    ));
  };

  const renderCursor = (session: RemoteSession): ReactElement | null => {
    if (session.cursor === null) {
      return null;
    }
    return (
      <div key={session.sessionId} className={styles.cursor} style={{transform: `translate(${session.cursor.x * zoom}px, ${session.cursor.y * zoom}px)`}}>
        <svg className={styles.cursorArrow} viewBox="0 0 20 20" aria-hidden="true">
          <path d="M3 2 L17 10 L10 11.5 L7 18 Z" fill={session.color} stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
        </svg>
        <div className={styles.cursorName} style={{backgroundColor: session.color}}>
          <Text val={session.participant.name} size={TextSize.XS} bold />
        </div>
      </div>
    );
  };

  return createPortal(
    <div className={styles.layer}>
      {sessions.flatMap(renderSelection)}
      {sessions.map(renderCursor)}
    </div>,
    backdrop
  );
}

